/**
 * camera_fault_store — 운전자 반응 시험용 "카메라 영상 장애 모사" 경량 외부 스토어.
 *
 * 개발자 도구다. 켜 두면(armed) 메인 창에서 Alt+1 로 끊김(저fps), Alt+2 로 순간 블랙을 걸 수
 * 있다. 실제 적용은 CameraMediaSinkRegistry 가 이 스토어를 구독해 모든 싱크(메인 HUD 타일 +
 * 보조 팝업)의 clone 트랙에 건다. 원본 트랙은 건드리지 않으므로 수신 FPS 통계는 실측 그대로다.
 *
 * camera_fps_cap_store 와 동일한 패턴(useSyncExternalStore 싱글턴).
 * 설정(settings)만 localStorage 에 저장하고, 실시간 상태(live)는 새로고침 시 항상 꺼진다.
 */
import { useSyncExternalStore } from "react";

export type CameraFaultSettings = {
  armed: boolean;
  choppyFps: number;
  blackMs: number;
};

export type CameraFaultLive = {
  choppyOn: boolean;
  /** 블랙이 끝나는 시각(Date.now 기준 ms). 0 이면 블랙 아님. */
  blackUntil: number;
};

export type CameraFaultState = CameraFaultSettings & CameraFaultLive;

export const CAMERA_FAULT_LIMITS = Object.freeze({
  choppyFps: { min: 1, max: 30 },
  blackMs: { min: 50, max: 5000 },
});

export const CAMERA_FAULT_STORAGE_KEY = "aw.cameraFaultSim.v1";

const DEFAULT_SETTINGS: CameraFaultSettings = {
  armed: false,
  choppyFps: 5,
  blackMs: 500,
};

function clamp(value: unknown, min: number, max: number, fallback: number): number {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, Math.round(num)));
}

function loadSettings(): CameraFaultSettings {
  try {
    const raw = window.localStorage.getItem(CAMERA_FAULT_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_SETTINGS };
    }
    const parsed = JSON.parse(raw) as Partial<CameraFaultSettings>;
    return {
      armed: parsed.armed === true,
      choppyFps: clamp(
        parsed.choppyFps,
        CAMERA_FAULT_LIMITS.choppyFps.min,
        CAMERA_FAULT_LIMITS.choppyFps.max,
        DEFAULT_SETTINGS.choppyFps,
      ),
      blackMs: clamp(
        parsed.blackMs,
        CAMERA_FAULT_LIMITS.blackMs.min,
        CAMERA_FAULT_LIMITS.blackMs.max,
        DEFAULT_SETTINGS.blackMs,
      ),
    };
  } catch (_err) {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(state: CameraFaultState): void {
  try {
    const settings: CameraFaultSettings = {
      armed: state.armed,
      choppyFps: state.choppyFps,
      blackMs: state.blackMs,
    };
    window.localStorage.setItem(CAMERA_FAULT_STORAGE_KEY, JSON.stringify(settings));
  } catch (_err) {
    // 저장 실패(사생활 보호 모드 등)는 무시 — 이번 세션 동안만 유지된다.
  }
}

let state: CameraFaultState = { ...loadSettings(), choppyOn: false, blackUntil: 0 };
let blackTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function clearBlackTimer(): void {
  if (blackTimer !== null) {
    clearTimeout(blackTimer);
    blackTimer = null;
  }
}

function setState(next: CameraFaultState, persist: boolean): void {
  state = next;
  if (persist) {
    saveSettings(next);
  }
  emit();
}

export function getCameraFaultSnapshot(): CameraFaultState {
  return state;
}

export function subscribeCameraFault(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function useCameraFault(): CameraFaultState {
  return useSyncExternalStore(
    subscribeCameraFault,
    getCameraFaultSnapshot,
    getCameraFaultSnapshot,
  );
}

/** 현재 블랙 구간인지 (armed 여부 포함). */
export function isCameraFaultBlackActive(
  s: CameraFaultState = state,
  now: number = Date.now(),
): boolean {
  return s.armed && s.blackUntil > now;
}

/** 현재 끊김(저fps)이 적용 중인지 (armed 여부 포함). */
export function isCameraFaultChoppyActive(s: CameraFaultState = state): boolean {
  return s.armed && s.choppyOn;
}

/** 모사 사용(armed) 토글. 끄면 걸려 있던 장애도 모두 해제한다. */
export function setCameraFaultArmed(armed: boolean): void {
  if (state.armed === armed) {
    return;
  }
  if (!armed) {
    clearBlackTimer();
    setState({ ...state, armed: false, choppyOn: false, blackUntil: 0 }, true);
    return;
  }
  setState({ ...state, armed: true }, true);
}

export function setCameraFaultChoppyFps(value: number): void {
  const next = clamp(
    value,
    CAMERA_FAULT_LIMITS.choppyFps.min,
    CAMERA_FAULT_LIMITS.choppyFps.max,
    state.choppyFps,
  );
  if (next === state.choppyFps) {
    return;
  }
  setState({ ...state, choppyFps: next }, true);
}

export function setCameraFaultBlackMs(value: number): void {
  const next = clamp(
    value,
    CAMERA_FAULT_LIMITS.blackMs.min,
    CAMERA_FAULT_LIMITS.blackMs.max,
    state.blackMs,
  );
  if (next === state.blackMs) {
    return;
  }
  setState({ ...state, blackMs: next }, true);
}

/** 끊김(저fps) 켜기/끄기. armed 가 아니면 무시. */
export function toggleCameraFaultChoppy(): void {
  if (!state.armed) {
    return;
  }
  setState({ ...state, choppyOn: !state.choppyOn }, false);
}

/** 순간 블랙을 blackMs 동안 건다(재트리거 시 연장). armed 가 아니면 무시. */
export function triggerCameraFaultBlack(now: number = Date.now()): void {
  if (!state.armed) {
    return;
  }
  clearBlackTimer();
  const blackUntil = now + state.blackMs;
  setState({ ...state, blackUntil }, false);
  // 만료 시 스냅샷을 바꿔 구독자(레지스트리 복구 · HUD 표시)가 다시 평가하게 한다.
  blackTimer = setTimeout(() => {
    blackTimer = null;
    if (state.blackUntil === blackUntil) {
      setState({ ...state, blackUntil: 0 }, false);
    }
  }, Math.max(0, blackUntil - Date.now()));
}

function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") {
    return false;
  }
  const tag = el.tagName.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}

/**
 * 메인 창 keydown 처리기. Alt+1: 끊김 토글, Alt+2: 순간 블랙.
 * 처리했으면 true. armed 아님 / 키 반복 / 입력칸 포커스면 무시(false).
 * 방향키·페달 등 직접제어 키와 겹치지 않도록 Alt+숫자만 쓴다(F키 사용 금지).
 */
export function handleCameraFaultKeyDown(event: KeyboardEvent): boolean {
  if (!state.armed || !event.altKey || event.repeat) {
    return false;
  }
  if (event.code !== "Digit1" && event.code !== "Digit2") {
    return false;
  }
  if (isEditableTarget(event.target) || isEditableTarget(document.activeElement)) {
    return false;
  }
  event.preventDefault();
  if (event.code === "Digit1") {
    toggleCameraFaultChoppy();
  } else {
    triggerCameraFaultBlack();
  }
  return true;
}

/** 메인 창 window 에 키 처리기를 설치한다. 해제 함수를 반환. */
export function installCameraFaultKeyHandler(target: Window = window): () => void {
  const listener = (event: KeyboardEvent) => {
    handleCameraFaultKeyDown(event);
  };
  target.addEventListener("keydown", listener, true);
  return () => target.removeEventListener("keydown", listener, true);
}

/** 테스트용: 설정/상태를 기본값으로 되돌린다(저장소는 건드리지 않음). */
export function resetCameraFaultStoreForTests(): void {
  clearBlackTimer();
  state = { ...DEFAULT_SETTINGS, choppyOn: false, blackUntil: 0 };
  emit();
}

/** 테스트용: localStorage 에서 설정을 다시 읽는다. */
export function reloadCameraFaultSettingsForTests(): void {
  clearBlackTimer();
  state = { ...loadSettings(), choppyOn: false, blackUntil: 0 };
  emit();
}
